import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getAddressesAction, cleanAddressAction, getAddressAction } from '../../redux/actions/address';
import LocalsEdit from './Edit';
import strings from '../../i18n/language';
import { instance } from '../../axios';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import NfcRoundedIcon from '@mui/icons-material/NfcRounded';
import AddressDelete from '../../dialog/AddressDelete';

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const ActionBtn = ({ onClick, title, color, children }) => (
  <Tooltip title={title}>
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: MUTED, borderRadius: 6,
        width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = color; e.currentTarget.style.background = `${color}18`; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; e.currentTarget.style.background = 'none'; }}
    >
      {children}
    </button>
  </Tooltip>
);

const LocalsList = () => {
  const addresses = useSelector((state) => state.address.addresses);
  const isLoadingAddresses = useSelector((state) => state.address.isLoadingAddresses);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [addressDeleteId, setAddressDeleteId] = useState(null);
  const [nfcUrl, setNfcUrl] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedCompany) dispatch(getAddressesAction(selectedCompany.id));
  }, [selectedCompany, dispatch]);

  const handleGenerateNfcUrl = async (local) => {
    if (!selectedCompany?.code) return;
    try {
      const content = encodeURIComponent(`${selectedCompany.code};${local.id}`);
      const response = await instance.get(`/utils/encrypt/${content}`);
      // Never show a tag URL without its code (e.g. an HTML answer)
      if (!response.data?.result) throw new Error('No encrypted content in the answer');
      setNfcUrl(`https://fidelize.org/scan?code=${response.data.result}`);
    } catch (error) {
      toast.error('Error al generar la URL');
    }
  };

  const handleCopyNfcUrl = async () => {
    try {
      await navigator.clipboard.writeText(nfcUrl);
      toast.success('URL copiada al portapapeles');
    } catch (error) {
      toast.error('No se pudo copiar — selecciona el texto manualmente');
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <div style={{
        background: GLASS,
        border: `1px solid ${BORDER}`,
        borderRadius: 20,
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <div>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              Locales
            </Typography>
            {addresses && addresses.length > 0 && (
              <Typography style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                {addresses.length} {addresses.length === 1 ? 'local' : 'locales'}
              </Typography>
            )}
          </div>
          <button
            onClick={() => { dispatch(cleanAddressAction()); setOpenModalEdit(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: BRAND, color: '#fff',
              border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(59,149,232,0.3)',
              letterSpacing: 0.2,
            }}>
            <AddRoundedIcon style={{ fontSize: 16 }} />
            Nuevo local
          </button>
        </div>

        {isLoadingAddresses && (
          <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { background: BRAND } }} />
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_LOCALS_HEADER_NAME}</ColHeader>
                <ColHeader>{strings.LABEL_LOCALS_HEADER_ADDRESS}</ColHeader>
                <ColHeader>{strings.LABEL_LOCALS_HEADER_PHONE_NUMBER}</ColHeader>
                <ColHeader style={{ width: 80 }}></ColHeader>
              </tr>
            </thead>
            <tbody>
              {addresses && addresses.length === 0 && !isLoadingAddresses && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 48 }}>
                    <StorefrontRoundedIcon style={{ fontSize: 32, color: MUTED, display: 'block', margin: '0 auto 10px' }} />
                    <span style={{ fontSize: 13, color: MUTED }}>Sin locales</span>
                  </td>
                </tr>
              )}
              {addresses && addresses.map((local) => (
                <tr key={local.id}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  style={{ transition: 'background 0.1s' }}
                >
                  <Td style={{ color: '#e8f0fe', fontWeight: 500 }}>{local.label}</Td>
                  <Td style={{ color: MUTED }}>{local.address}</Td>
                  <Td style={{ color: MUTED, fontFamily: 'monospace', fontSize: 12 }}>{local.phoneNumber || '—'}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <ActionBtn
                        title="Generar URL NFC"
                        color={BRAND}
                        onClick={() => handleGenerateNfcUrl(local)}
                      >
                        <NfcRoundedIcon style={{ fontSize: 15 }} />
                      </ActionBtn>
                      <ActionBtn
                        title="Editar"
                        color="#e8f0fe"
                        onClick={() => { dispatch(getAddressAction(local.id)); setOpenModalEdit(true); }}
                      >
                        <EditRoundedIcon style={{ fontSize: 15 }} />
                      </ActionBtn>
                      <ActionBtn
                        title="Eliminar"
                        color="#e05c5c"
                        onClick={() => { setAddressDeleteId(local.id); setOpenModalDelete(true); }}
                      >
                        <DeleteOutlineRoundedIcon style={{ fontSize: 15 }} />
                      </ActionBtn>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddressDelete open={openModalDelete} setOpen={setOpenModalDelete} addressDeleteId={addressDeleteId} />
      <LocalsEdit open={openModalEdit} onClose={() => setOpenModalEdit(false)} />

      <Dialog open={!!nfcUrl} onClose={() => setNfcUrl(null)} maxWidth="sm" fullWidth>
        <DialogTitle>URL para tarjeta NFC</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={nfcUrl || ''}
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNfcUrl(null)}>{strings.LABEL_BUTTON_CANCEL}</Button>
          <Button variant="contained" onClick={handleCopyNfcUrl}>Copiar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocalsList;
